/**
 * Utilities for extracting summary data from SummaryGrid AG Grid instances
 *
 * Extracts the summary row data (Monthly Premium, Annual Premium) from the
 * AG Grid API and transforms it into a format suitable for Excel export.
 */

import { GridApi } from "ag-grid-community";
import { Worksheet } from "exceljs";

import { mergePlanSpanCells, mergeGroupSpanCells } from "../worksheetLayout";

import type { ExcelRow } from "./builder";
import type { PlanColumnSpans } from "../types";

/**
 * Extract summary rows from a SummaryGrid's GridApi
 *
 * The SummaryGrid stores data with structure:
 * {
 *   summaryType: "Monthly Premium",
 *   [planId]: 12345.67,
 *   [planId]: 23456.78,
 *   ...
 * }
 *
 * This function transforms it to ExcelRow format:
 * {
 *   label: "Monthly Premium",
 *   values: { [planId]: 12345.67, [planId]: 23456.78, ... }
 * }
 */
export function extractSummaryRows(gridApi: GridApi): ExcelRow[] {
  const summaryRows: ExcelRow[] = [];

  gridApi.forEachNode((node) => {
    if (node.data) {
      const { summaryType, isGroupTotal, ...planValues } = node.data as Record<
        string,
        string | number | boolean | null
      >;

      // Drop empty cells (alternate plans, non-first cells in group totals) so
      // values are reliably string | number for downstream consumers.
      const values: Record<string, string | number> = {};
      Object.entries(planValues).forEach(([planId, value]) => {
        if (typeof value === "string" || typeof value === "number") {
          values[planId] = value;
        }
      });

      summaryRows.push({
        label: String(summaryType),
        values,
        isGroupTotal: Boolean(isGroupTotal),
      });
    }
  });

  return summaryRows;
}

/**
 * Append summary rows to an Excel worksheet
 *
 * Adds a "Summary" header row followed by the summary data rows.
 * Values are formatted as currency and right-aligned.
 */
export function appendSummaryToWorksheet(
  worksheet: Worksheet,
  summaryRows: ExcelRow[],
  planIdsInOrder: string[],
  planColumnSpans?: PlanColumnSpans,
  planGroupIdByPlanId?: Record<string, string>,
): void {
  // Add summary section header
  worksheet.addRow(["Summary"]);

  // Add summary data rows, aligning values with the correct columns
  summaryRows.forEach((row) => {
    const rowData: (string | number)[] = [row.label];

    // For each plan column in order, get the value from the row
    planIdsInOrder.forEach((planId) => {
      const value = row.values[planId];
      const span = planColumnSpans?.[planId] ?? 1;
      const displayValue =
        typeof value === "number" && value !== null
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value)
          : (value ?? "");

      // Format as currency if it's a number
      rowData.push(displayValue);

      for (let index = 1; index < span; index += 1) {
        rowData.push("");
      }
    });

    const addedRow = worksheet.addRow(rowData);

    if (row.isGroupTotal && planGroupIdByPlanId) {
      mergeGroupSpanCells(
        worksheet,
        addedRow.number,
        planIdsInOrder,
        planGroupIdByPlanId,
        planColumnSpans,
      );
    } else {
      mergePlanSpanCells(
        worksheet,
        addedRow.number,
        planIdsInOrder,
        planColumnSpans,
      );
    }

    addedRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
      if (colNumber > 1) {
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "thin" },
          left: { style: "thin" },
          right: { style: "thin" },
        };
      } else {
        cell.alignment = { horizontal: "left", vertical: "middle" };
      }
    });
  });
}
